/**
 * Display helpers for WhatsApp people (D-117). A WhatsApp privacy id (`…@lid`) is never a phone:
 * its digits must never be formatted or shown as a number.
 */

export const HIDDEN_PHONE_HE = 'מספר מוסתר';
export const HIDDEN_PHONE_LONG_HE = 'מספר מוסתר על ידי וואטסאפ';

/** true for a WhatsApp privacy id (`<digits>@lid`) */
export function isLidWaId(waId: string | null | undefined): boolean {
  return typeof waId === 'string' && waId.endsWith('@lid');
}

const PHONE_WA_ID = /^(\d{6,15})(?::\d+)?@(c\.us|s\.whatsapp\.net)$/;

/**
 * Digits of a real phone number, or null. Never returns the digits of an `@lid` / `@g.us` id.
 * With `@`: only `@c.us` / `@s.whatsapp.net` ids count. Without `@`: a free-typed number
 * (`054-111-2233`, `+44 7700 900412`, `00972…`), 8–15 digits, a local `0…` mobile becomes `972…`.
 */
export function phoneDigitsOf(v: string | null | undefined): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s) return null;
  if (s.includes('@')) {
    const m = PHONE_WA_ID.exec(s);
    return m ? m[1]! : null;
  }
  let d = s.replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 10 && d.startsWith('0')) d = `972${d.slice(1)}`;
  return d.length >= 8 && d.length <= 15 ? d : null;
}

/** "+972 52-555-0142" for Israeli numbers, "+<digits>" for the rest */
export function formatIlPhone(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.startsWith('972')) {
    const n = d.slice(3);
    if (n.length === 9) return `+972 ${n.slice(0, 2)}-${n.slice(2, 5)}-${n.slice(5)}`;
    if (n.length === 8) return `+972 ${n.slice(0, 1)}-${n.slice(1, 4)}-${n.slice(4)}`;
  }
  return `+${d}`;
}

export interface PersonInput {
  name?: string | null;
  deviceName?: string | null;
  /** WhatsApp Business verified name (D-125) */
  verifiedName?: string | null;
  pushName?: string | null;
  phone?: string | null;
  waId?: string | null;
}

export interface PersonLabel {
  /** never empty */
  primary: string;
  /** formatted phone, or HIDDEN_PHONE_LONG_HE, when primary is a name */
  secondary: string | null;
  /** formatted phone or null */
  phone: string | null;
  nameSource: 'ours' | 'device' | 'business' | 'whatsapp' | 'none';
  /** an @lid id with no known phone */
  phoneHidden: boolean;
}

/** Hebrew label of where the shown name came from (D-125) */
export const NAME_SOURCE_HE: Record<PersonLabel['nameSource'], string> = {
  ours: 'שם שלנו',
  device: 'שמור בטלפון',
  business: 'עסק מאומת',
  whatsapp: 'שם בוואטסאפ',
  none: '',
};

const clean = (v: string | null | undefined): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t ? t : null;
};

/** The digits of an `@lid` id (what OpenWA echoes back as a "phone" when it cannot resolve the number). */
function lidDigitsOf(waId: string | null | undefined): string | null {
  if (!isLidWaId(waId)) return null;
  return (waId as string).split('@')[0]!.replace(/\D/g, '') || null;
}

/**
 * Digits of a STORED phone (Contact.phone / Message.senderPhone) that belongs to `waId`, or null.
 * A stored "phone" equal to the digits of the person's own `@lid` id is the privacy id, not a number.
 */
export function storedPhoneDigits(phone: string | null | undefined, waId?: string | null): string | null {
  const d = phoneDigitsOf(phone);
  return d && d !== lidDigitsOf(waId) ? d : null;
}

/**
 * Precedence (D-125): our name > the phone-saved name > the WhatsApp Business verified name > "~" + WhatsApp push name
 * > the phone > "מספר מוסתר".
 */
export function personLabel(p: PersonInput): PersonLabel {
  const digits = storedPhoneDigits(p.phone, p.waId) ?? phoneDigitsOf(p.waId);
  const phone = digits ? formatIlPhone(digits) : null;
  const phoneHidden = !digits && isLidWaId(p.waId);
  const name = clean(p.name);
  const deviceName = clean(p.deviceName);
  const verifiedName = clean(p.verifiedName);
  const pushName = clean(p.pushName);

  let primary: string | null = null;
  let nameSource: PersonLabel['nameSource'] = 'none';
  if (name) {
    primary = name;
    nameSource = 'ours';
  } else if (deviceName) {
    primary = deviceName;
    nameSource = 'device';
  } else if (verifiedName) {
    primary = verifiedName;
    nameSource = 'business';
  } else if (pushName) {
    primary = `~${pushName}`;
    nameSource = 'whatsapp';
  }

  if (primary === null) {
    return {
      primary: phone ?? (phoneHidden ? HIDDEN_PHONE_HE : 'לא ידוע'),
      secondary: null,
      phone,
      nameSource,
      phoneHidden,
    };
  }
  return { primary, secondary: phone ?? (phoneHidden ? HIDDEN_PHONE_LONG_HE : null), phone, nameSource, phoneHidden };
}
